import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { IncidentCreatedBy, IncidentSearchEvent, IncidentStatus } from '../incident-models';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { FilteringService } from 'src/app/shared/services/filtering.service';
import { distinctUntilChanged, switchMap, tap } from 'rxjs';
import IncidentService from 'src/app/shared/services/incident.service';
import { PagerBase } from 'src/app/shared/models/pagerBase';
interface IncidentFilterState {
  query: string | null;
  organizationId: number | null;
  createdBy: IncidentCreatedBy | null;
  status: IncidentStatus | null;
  sort: Sort | null;
  page: PageEvent;
}

export enum IncidentsListQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  status = 'status',
  createdBy = 'createdBy',
}

@Injectable()
export class IncidentListStateService extends PagerBase {
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  filteringService = inject(FilteringService);
  incidentService = inject(IncidentService);
  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);
  public loading: WritableSignal<boolean> = signal<boolean>(false);
  public resetOrgFilterSignal = signal<{ reset: boolean }>({ reset: false });
  public pageSizeOptions = signal<number[]>(this.pageSizes);

  public filters: WritableSignal<IncidentFilterState> = signal<IncidentFilterState>({
    organizationId: this.queryParamMap$()?.get(IncidentsListQueryParams.organizationId)
      ? parseInt(this.queryParamMap$()?.get(IncidentsListQueryParams.organizationId) ?? '0')
      : null,
    query: this.queryParamMap$()?.get(IncidentsListQueryParams.query)
      ? decodeURIComponent(this.queryParamMap$()?.get(IncidentsListQueryParams.query) ?? '')
      : null,
    status: (() => {
      const param = this.queryParamMap$()?.get(IncidentsListQueryParams.status);
      return param ? this.filteringService.safeCastEnum(IncidentStatus, param) : null;
    })(),
    createdBy: (() => {
      const param = this.queryParamMap$()?.get(IncidentsListQueryParams.createdBy);
      return param ? this.filteringService.safeCastEnum(IncidentCreatedBy, parseInt(param)) : null;
    })(),
    sort: null,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    },
  });

  public incidents$ = toObservable(this.filters).pipe(
    distinctUntilChanged(),
    takeUntilDestroyed(),
    tap(() => this.loading.set(true)),
    switchMap(filters => {
      const prefix = 'OK';
      const searchEvent = <IncidentSearchEvent>{
        query: filters.query
          ? filters.query?.toLocaleLowerCase().startsWith(prefix.toLocaleLowerCase())
            ? filters.query.slice(prefix.length + 1)
            : filters.query
          : null,
        paging: filters.page,
        sorting: filters.sort,
        organizationId: filters.organizationId,
        status: filters.status,
        createdBy: filters.createdBy,
      };

      return this.incidentService.getIncidents(searchEvent);
    }),
    tap(() => this.loading.set(false))
  );

  triggerUpdate() {
    this.filters.update(f => ({ ...f }));
  }

  public resetFilter() {
    this.filters.update(current => ({
      ...current,
      query: null,
      organizationId: null,
      createdBy: null,
      status: null,
      sort: null,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
    }));

    this.resetOrgFilterSignal.set({ reset: true });
  }
}
